import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import Layout from './Layout';
import {useLocation} from 'react-router-dom';
import Container from 'components/Container';
import SwitchHeaderSubtitle from 'features/SwitchConnectivity/components/SwitchHeaderSubtitle';
import LayoutHeader, {LayoutHeaderIconTypes} from 'components/LayoutHeader';
import SiteIDSearch from 'components/SiteIDSearch';
import SwitchConnectivityInstance from 'features/SwitchConnectivity';
import {getSafeBaseUrl, buildSafeUrlWithHash} from 'utils/urlSanitization';

//#region styled-components

const StyledSwitchHeader = styled.div`
  display: grid;
  grid-template-areas:
    'headerTitle search'
    'headerSubtitle search';
  grid-template-columns: auto 388px;
  #header-text {
    grid-area: headerTitle;
  }
  #header-subtitle {
    grid-area: headerSubtitle;
    align-self: flex-end;
  }
  #siteid-search-container {
    grid-area: search;
  }
`;

//#endregion styled-components

function useQuery() {
  const {search} = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function SwitchConnectivity() {
  let query = useQuery();

  const [siteIdName, setSiteIdName] = useState<string | undefined>(
    query.get('siteId') || undefined
  );

  const [error, setError] = useState<string>('');
  const [canGetSwitchData, setCanGetSwitchData] = useState<boolean>(false);

  const [sites, setSites] = useState<{siteId: string; deviceNames: string[]}[]>([]);

  const [intersectedTabs, setIntersectedTabs] = useState<Record<string, number>>({});
  const [highlightedTab, setHighlightedTab] = useState<string>('');

  const updateIntersectedTabs = (hash: string, intersectingRatio: number) => {
    if (typeof window === 'undefined') return;

    const baseUrl = getSafeBaseUrl();
    const newIntersectedTabs = intersectedTabs;
    newIntersectedTabs[hash] = intersectingRatio;

    let highestRatio = 0;
    let newHash = '';

    Object.entries(newIntersectedTabs).forEach(entry => {
      if (entry[1] >= highestRatio) {
        highestRatio = entry[1];
        newHash = entry[0];
      }
    });

    setHighlightedTab(newHash);
    setIntersectedTabs(newIntersectedTabs);
    window.history.replaceState(null, '', buildSafeUrlWithHash(baseUrl, newHash));
  };

  useEffect(() => {
    const siteIdQuery: string[] = query.getAll('siteId');

    if (siteIdQuery.length !== 1) {
      setCanGetSwitchData(false);
      return;
    } else if (siteIdQuery[0].length !== 7) {
      setCanGetSwitchData(false);
      setError('Invalid SiteId');
      return;
    }

    const deviceNames = query.getAll('deviceName');

    setSites([{siteId: siteIdQuery[0], deviceNames}]);

    setCanGetSwitchData(true);
  }, [query]);

  return (
    <Layout
      headerContent={
        <StyledSwitchHeader>
          <LayoutHeader title={'Switch Connectivity'} icon={LayoutHeaderIconTypes.Switch} />
          {canGetSwitchData && <SiteIDSearch urlType={'switch'} setError={setError} />}
          <SwitchHeaderSubtitle siteId={siteIdName} />
        </StyledSwitchHeader>
      }
    >
      <Container>
        {sites.map(site => {
          return (
            <SwitchConnectivityInstance
              siteId={site.siteId}
              deviceNames={site.deviceNames}
              key={`switch-${site.siteId}`}
              updateIntersectedTabs={updateIntersectedTabs}
            />
          );
        })}
      </Container>
    </Layout>
  );
}
